import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const orderId = params.get("order_id");
  const isCod = params.get("cod") === "1";
  const { clear } = useCart();
  const [status, setStatus] = useState(isCod ? "paid" : "checking");
  const [amount, setAmount] = useState(0);
  const attempts = useRef(0);

  useEffect(() => {
    if (isCod && orderId) {
      // COD order — fetch details once
      api.get(`/orders/${orderId}`).then(r => {
        setAmount(r.data.amount || 0);
        setStatus("paid");
        clear();
      }).catch(() => setStatus("error"));
      return;
    }
    if (!sid) return setStatus("error");
    const poll = async () => {
      if (attempts.current >= 8) return setStatus("timeout");
      attempts.current++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setAmount(data.amount || 0);
        if (data.payment_status === "paid") { setStatus("paid"); clear(); return; }
        if (data.status === "expired" || data.status === "failed") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, orderId, isCod]);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center" data-testid="payment-success-page">
      {status === "checking" && <p className="font-serif text-3xl">Confirming your order...</p>}
      {status === "paid" && (
        <>
          <CheckCircle size={64} weight="light" color="#2D4A22" />
          <div className="overline text-[#4A1525] mt-6 mb-2">{isCod ? "Order placed" : "Order confirmed"}</div>
          <h1 className="font-serif text-5xl md:text-6xl mb-4">Thank you.</h1>
          <p className="text-[#5C5C5C] max-w-md mb-8">
            {isCod
              ? `Your order for ₹${amount.toFixed(0)} is placed. Please have exact change ready — our courier will collect payment on delivery. A confirmation is on its way.`
              : `Your order for ₹${amount.toFixed(0)} is confirmed. A confirmation is on its way, packaged discreetly.`}
          </p>
          <Link to="/shop" className="bg-[#4A1525] text-[#FAF8F5] px-8 py-4 overline" data-testid="continue-shopping">Continue shopping</Link>
        </>
      )}
      {(status === "failed" || status === "timeout" || status === "error") && (
        <>
          <XCircle size={64} weight="light" color="#7A2020" />
          <h1 className="font-serif text-4xl mt-6 mb-4">We couldn't confirm your payment.</h1>
          <Link to="/checkout" className="overline underline underline-offset-4">Try again</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center" data-testid="payment-cancel-page">
      <XCircle size={64} weight="light" color="#7A2020" />
      <h1 className="font-serif text-5xl mt-6 mb-4">Payment cancelled.</h1>
      <p className="text-[#5C5C5C] mb-8">Your bag is still saved. Take your time.</p>
      <Link to="/checkout" className="bg-[#4A1525] text-[#FAF8F5] px-8 py-4 overline">Return to checkout</Link>
    </div>
  );
}
